import React, { Component } from 'react';
import PropTypes from 'prop-types';
import confetti from 'canvas-confetti';

import { SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG } from 'api/utilityActionTypes';

import {
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER,
  hasRoleAdmin,
  hasRoleGroupAdmin,
  hasRolePartnerAdmin,
  hasRolePartnerAccounting,
  hasRolePartnerPropertyManager,
  hasRoleViewRevenue,
} from 'common/utility/userRoles';

import {
  UTILITY_ANNOUNCEMENT_TYPE_RENTERS_INSURANCE,
  UTILITY_PARTNER_STATUS_PENDING_DEMO,
} from 'common/utility/constants';

import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withLocationQuery from 'client/helpers/withLocationQuery';
import { addQueryParam, removeQueryParam } from 'client/helpers/queryParamHelpers';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import { withUtilityApiState } from 'client/helpers/withApiState';
import { SCREEN_ID_UTILITY_PARTNER_OVERVIEW } from 'client/constants/screenIds';

import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityPayoutActions from 'client/actions/UtilityPayout';
import PropertyManagementSoftwareActions from 'client/actions/PropertyManagementSoftware';
import UtilityProfitTipsActions from 'client/actions/UtilityProfitTips';
import UtilityPartnerGroupActions from 'client/actions/UtilityPartnerGroup';
import AnnouncementsActions from 'client/actions/Announcements';
import UtilityMetabaseChartsActions from 'client/actions/UtilityMetabaseCharts';
import UPTips from 'client/components/UPTips/UPTips';
import SrBox from 'client/components/SrBox';
import SrPartnerList from 'client/components/SrPartnerList';
import UPHubspotWidget from 'client/components/UPHubspotWidget';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import UPActivationLink from 'client/components/UPActivationLink/UPActivationLink';
import UpSelfOnboarding from 'client/components/UPSelfOnboarding/UpSelfOnboarding';
import UPActivationsSummary from 'client/components/UPActivationsSummary/UPActivationsSummary';
import SrUtilityProfitActivationsDataTable from 'client/components/SrUtilityProfitActivationsDataTable/SrUtilityProfitActivationsDataTable';
import SrUtilityProfitAddressesDataTable from 'client/components/SrUtilityProfitAddressesDataTable/SrUtilityProfitAddressesDataTable';
import SrUtilityProfitActivationsTeam from 'client/components/SrUtilityProfitActivationsDataTable/SrUtilityProfitActivationsTeam';
import UPAccountManagerCard from 'client/components/UPAccountManagerCard/UPAccountManagerCard';
import UPTenantSupportCard from 'client/components/UPTenantSupportCard/UPTenantSupportCard';
import UpSelfOnboardingConnectStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingConnectStep';
import RenterInsuranceWaitlistPrompt from 'client/components/RenterInsuranceWaitlistPrompt/RenterInsuranceWaitlistPrompt';
import UPTenantLiabilityBannerCard from 'client/components/UPTenantLiabilityBanner/UPTenantLiabilityBannerCard';
import UPEnableTenantLiabilityModal from 'client/components/UPEnableTenantLiabilityModal/UPEnableTenantLiabilityModal';
import { shouldViewTenantLiabilityBanners } from 'common/utility/helpers';

const getCanAccessPartner = (utilityUser, utilityPartner, currentPartner) => {
  if (!utilityPartner) {
    return false;
  }
  if (!utilityUser) {
    return false;
  }
  if (hasRoleAdmin(utilityUser.roles)) {
    return true;
  }
  if (hasRoleGroupAdmin(utilityUser.roles)) {
    return utilityPartner.utilityPartnerGroupId === currentPartner.utilityPartnerGroupId;
  }
  if (hasRolePartnerPropertyManager(utilityUser.roles)) {
    return !!utilityUser.partners.find((p) => p.id === utilityPartner.id);
  }
  return utilityPartner.id === currentPartner.id;
};

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_PARTNER_OVERVIEW)
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withUtilityApiState({
  getUtilityPartnerApiState: {
    action: SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG,
  },
})
@branch(({ match }) => ({
  utilityUser: ['utilityUser'],
  utilityTypes: ['utilityActivations', 'types'],
  currentPartner: ['utilityPartners', 'currentPartner'],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  revenueDetails: ['utilityPartners', 'revenueDetailsBySlug', match.params.partnerSlug],
  utilityPartnerStripeAccounts: [
    'utilityPartners',
    'stripeAccountsByPartnerSlug',
    match.params.partnerSlug,
  ],
  utilityPayouts: ['utilityPayouts', 'pendingPayouts', 'byPartnerSlug', match.params.partnerSlug],
  propertyManagementSoftwareList: ['propertyManagementSoftware', 'list'],
  utilityProfitTips: ['utilityProfitTips'],
}))
@branch(({ utilityPartner, utilityUser }) => {
  if (!utilityPartner || !utilityUser) {
    return {};
  }

  const cursors = {
    showResidualRevenuePrompt: [
      'utilityResidualRevenue',
      'showPromptByPartnerId',
      utilityPartner.id,
    ],
    showRentersInsuranceWaitlist: [
      'utilityAnnouncements',
      'byPartnerId',
      utilityPartner.id,
      utilityUser?.id,
      UTILITY_ANNOUNCEMENT_TYPE_RENTERS_INSURANCE,
    ],
    metabaseCharts: ['utilityMetabaseCharts', 'byPartnerId', utilityPartner.id],
  };

  if (utilityPartner.utilityPartnerGroupId) {
    cursors.utilityPartnerGroup = [
      'utilityPartnerGroups',
      'byId',
      utilityPartner.utilityPartnerGroupId,
    ];
  }
  return cursors;
})
@withLocationQuery
class UtilityPartnerOverviewPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Profit Platform',
  });

  constructor(props) {
    super(props);
    this.state = {
      skipSelfOnboarding: false,
      showSelfOnboardingCompletedModal: false,
      showSelfOnboardingConnectStep: false,
      rentersInsuranceIsOpen: true,
      showEnableTenantLiabilityModal: false,
    };

    this.unlistedAddressesTableRef = null;
  }

  static propTypes = {
    currentPartner: PropTypes.object,
    utilityPartner: PropTypes.object,
    utilityTypes: PropTypes.array,
    getUtilityPartnerApiState: PropTypes.object.isRequired,
    isTransitioning: PropTypes.bool,
    metabaseCharts: PropTypes.array,
  };

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPayoutActions.getPendingPayoutsByPartnerSlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityPartnerActions.getStripeAccounts(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityPartnerActions.getRevenueDetails(inject, {
        slug: match.params.partnerSlug,
        forceRefresh: true,
      }),
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }).then((utilityPartner) => {
        if (utilityPartner) {
          const promises = [
            UtilityMetabaseChartsActions.getByPartnerId(inject, {
              partnerId: utilityPartner.id,
            }),
            AnnouncementsActions.showPrompt(inject, {
              utilityPartnerId: utilityPartner.id,
              type: UTILITY_ANNOUNCEMENT_TYPE_RENTERS_INSURANCE,
            }),
          ];

          if (utilityPartner.utilityPartnerGroupId) {
            promises.push(
              UtilityPartnerGroupActions.getById(inject, {
                id: utilityPartner.utilityPartnerGroupId,
              }),
            );
          }

          return Promise.all(promises);
        }
      }),
      UtilityActivationActions.getTypesList(inject),
      PropertyManagementSoftwareActions.getList(inject),
      UtilityProfitTipsActions.getList(inject),
    ]);
  }

  componentDidMount() {
    const { match, history, locationQuery } = this.props;
    if (locationQuery.pmsSyncStep) {
      this.setState({ showSelfOnboardingConnectStep: true });
      history.replace(`${match.url}${locationQuery.pmsId ? `?pmsId=${locationQuery.pmsId}` : ''}`);
    }
    this._syncTenantLiabilityModalFromQuery();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.locationQuery?.lm !== this.props.locationQuery?.lm ||
      prevProps.utilityPartner !== this.props.utilityPartner
    ) {
      this._syncTenantLiabilityModalFromQuery();
    }
  }

  _syncTenantLiabilityModalFromQuery = () => {
    const shouldShow =
      this.props.locationQuery?.lm === '1' &&
      shouldViewTenantLiabilityBanners(this.props.utilityPartner);

    if (shouldShow !== this.state.showEnableTenantLiabilityModal) {
      this.setState({ showEnableTenantLiabilityModal: shouldShow });
    }
  };

  _onUnlistedAddressesRef = (unlistedAddressesRef) => {
    const { location } = this.props;

    this.unlistedAddressesTableRef = unlistedAddressesRef;

    if (location.search.includes('scrollTo=unlistedAddresses')) {
      setTimeout(() => {
        this._focusUnlistedAddresses();
      }, 1500);
    }
  };

  _focusUnlistedAddresses = () => {
    const element = this.unlistedAddressesTableRef;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  _onCompleteSelfOnboarding = () => {
    this.setState({ showSelfOnboardingCompletedModal: true });

    setTimeout(() => {
      this.setState({ showSelfOnboardingCompletedModal: false });
    }, 7000);

    setTimeout(() => {
      confetti({
        particleCount: 950,
        spread: 130,
        decay: 0.965,
        startVelocity: 35,
        ticks: 550,
        zIndex: 1000,
      });
    }, 1000);
  };

  _onSetPropertyManagementSoftware = () => {
    const { dispatch, match } = this.props;

    dispatch(UtilityPartnerActions.getBySlug, {
      slug: match.params.partnerSlug,
      forceRefresh: true,
    }).then(() => {
      this.setState({ showSelfOnboardingConnectStep: false });
    });
  };

  render() {
    const {
      utilityUser,
      currentPartner,
      utilityPartner,
      utilityPayouts,
      revenueDetails,
      utilityTypes,
      getUtilityPartnerApiState,
      utilityPartnerStripeAccounts,
      isTransitioning,
      locationQuery,
      propertyManagementSoftwareList,
      utilityProfitTips,
      utilityPartnerGroup,
      metabaseCharts,
      showRentersInsuranceWaitlist,
    } = this.props;
    const {
      skipSelfOnboarding,
      showSelfOnboardingCompletedModal,
      showSelfOnboardingConnectStep,
      rentersInsuranceIsOpen,
      showEnableTenantLiabilityModal,
    } = this.state;
    const canAccessPartner = getCanAccessPartner(utilityUser, utilityPartner, currentPartner);

    const isPartnerAccounting =
      hasRolePartnerAccounting(utilityUser.roles) ||
      hasRolePartnerAdmin(utilityUser.roles) ||
      hasRoleAdmin(utilityUser.roles);

    if (
      getUtilityPartnerApiState.inProgress ||
      isTransitioning ||
      !utilityPartner ||
      !canAccessPartner
    ) {
      return <div className="loading-ellipses" />;
    }

    const canViewRevenue =
      (hasRoleViewRevenue(utilityUser.roles) || hasRoleAdmin(utilityUser.roles)) &&
      utilityPartner.experienceConfiguration?.detailedModeActive;
    const canViewTips =
      (utilityProfitTips?.length > 0 || accountManager) &&
      utilityPartner.experienceConfiguration?.detailedModeActive;

    if (utilityPartner.status === UTILITY_PARTNER_STATUS_PENDING_DEMO && !skipSelfOnboarding) {
      return (
        <UpSelfOnboarding
          utilityPartner={utilityPartner}
          utilityUser={utilityUser}
          utilityPartnerGroup={utilityPartnerGroup}
          propertyManagementSoftwareList={propertyManagementSoftwareList || []}
          onSkip={() => this.setState({ skipSelfOnboarding: true })}
          onComplete={this._onCompleteSelfOnboarding}
        />
      );
    }

    if (showSelfOnboardingConnectStep) {
      return (
        <UpSelfOnboardingConnectStep
          utilityPartner={utilityPartner}
          propertyManagementSoftwareList={propertyManagementSoftwareList || []}
          onNext={this._onSetPropertyManagementSoftware}
          showSkipForNow
        />
      );
    }

    if (showRentersInsuranceWaitlist && rentersInsuranceIsOpen) {
      return (
        <RenterInsuranceWaitlistPrompt
          utilityPartner={utilityPartner}
          utilityUser={utilityUser}
          onClose={() => this.setState({ rentersInsuranceIsOpen: false })}
        />
      );
    }

    const accountManager = utilityPartner.accountManager;
    const hasMetabaseCharts = metabaseCharts?.length > 0;

    const navbarLinks = !hasMetabaseCharts
      ? null
      : [
          {
            label: 'Home',
            value: `/utility-profit/${utilityPartner?.slug}`,
          },
          {
            label: 'Internet Amenity',
            value: `/utility-profit/${utilityPartner?.slug}/internet-amenity`,
            icon:
              new Date() > new Date('2025-02-18')
                ? null
                : {
                    url: '/images/new-feature-yellow.svg',
                    width: 74,
                    height: 17,
                    offset: {
                      top: -12,
                      right: -70,
                    },
                  },
          },
        ];

    return (
      <div className="SrPartnerPage">
        <SrPartnerPageV2
          navbarLinks={navbarLinks}
          navbarTitle={utilityPartner.name}
          navbarImage={utilityPartner.logoUrl}
          utilityUser={utilityUser}
          utilityPartner={utilityPartner}
          padding="0"
          contentPadding="0"
          maxWidth="1140px"
          navbarMaxWidth="1180px"
          redirectToLastLocation={false}
          backUrl={locationQuery?.backUrl || null}
        >
          <div className="SrUtilityPartnerPageContent">
            <SrPartnerSection>
              <SrBox padding="0 0 20px 0">
                <SrUtilityProfitActivationsTeam utilityPartner={utilityPartner} />
              </SrBox>
              <SrBox display="grid" gridTemplateColumns="3fr 2fr" gap="20px">
                <UPActivationLink utilityPartner={utilityPartner} />
                {shouldViewTenantLiabilityBanners(utilityPartner) && (
                  <UPTenantLiabilityBannerCard
                    onEnableClick={() => {
                      const { history, match, location } = this.props;
                      this.setState({ showEnableTenantLiabilityModal: true });
                      addQueryParam(history, location, match.url, 'lm', '1');
                    }}
                  />
                )}
              </SrBox>
              {canViewRevenue && (
                <UPActivationsSummary
                  utilityPayouts={utilityPayouts}
                  utilityPartner={utilityPartner}
                  collectedRevenue={revenueDetails?.totalCashOutAmount || 0}
                  expectedRevenue={revenueDetails?.expectedRevenueAmount || 0}
                  utilityPartnerStripeAccounts={utilityPartnerStripeAccounts}
                  isPartnerAccounting={isPartnerAccounting}
                />
              )}
              <SrUtilityProfitActivationsDataTable
                utilityTypes={utilityTypes || []}
                utilityPartner={utilityPartner}
                utilityUser={utilityUser}
                propertyManagementSoftwareList={propertyManagementSoftwareList}
                onSelectPropertyManagementSoftware={() => {
                  this.setState({ showSelfOnboardingConnectStep: true });
                }}
              />
              <SrBox paddingBottom="60px">
                <SrUtilityProfitAddressesDataTable
                  title="Synced Listings"
                  utilityTypes={utilityTypes || []}
                  utilityPartner={utilityPartner}
                  utilityPartnerSlug={utilityPartner?.slug}
                  liabilityInsuranceEnabled={
                    utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled
                  }
                />
              </SrBox>
            </SrPartnerSection>
            {canViewTips && (
              <SrPartnerSection title="Utility Profit Tips" padding="60px 0 80px 0">
                <SrPartnerList fourToTwoColumns>
                  {utilityProfitTips?.length > 0 && <UPTips quickTips={utilityProfitTips} />}
                  <SrBox display="flex" flexDirection="column" gap="20px">
                    {accountManager && (
                      <UPAccountManagerCard
                        name={accountManager?.name}
                        position={accountManager?.positionTitle}
                        email={accountManager?.email}
                        phone={accountManager?.phone}
                        profilePictureUrl={accountManager?.profilePictureUrl}
                        linkText={accountManager?.hyperlinkTitle}
                        linkUrl={accountManager?.hyperlinkUrl}
                      />
                    )}
                    <UPTenantSupportCard />
                  </SrBox>
                </SrPartnerList>
              </SrPartnerSection>
            )}
          </div>
        </SrPartnerPageV2>
        {showSelfOnboardingCompletedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Success! You can share your link to tenants now."
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            innerStyle={{ marginTop: '21vh' }}
            height="unset"
          />
        )}
        {showEnableTenantLiabilityModal && (
          <UPEnableTenantLiabilityModal
            utilityPartner={utilityPartner}
            onClose={() => {
              const { history, match, location } = this.props;
              this.setState({ showEnableTenantLiabilityModal: false });
              removeQueryParam(history, location, match.url, 'lm');
            }}
            onSuccess={() => {
              const { dispatch, match } = this.props;
              dispatch(UtilityPartnerActions.getBySlug, {
                slug: match.params.partnerSlug,
                forceRefresh: true,
              });
            }}
          />
        )}
        <UPHubspotWidget />
      </div>
    );
  }
}

UtilityPartnerOverviewPage.displayName = 'UtilityPartnerOverviewPage';

export default UtilityPartnerOverviewPage;
